import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, IconButton, Button, useTheme, Typography, TablePagination, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add } from 'iconsax-react';
import { Icon } from '@iconify/react';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { StyledMenu } from '@ui/components/common/Menu';
import { useMediaQuery } from '@mui/material';
import { Iconly } from 'react-iconly';
const DataTable = ({ data, columns, getRowId, searchValue = '', onSearchChange, searchPlaceholder, onFilterClick, filterLabel, showSearchFilter = true, showFilterButton = true, // Default to true to show filter button
filters = {}, onClearFilters, showFilterClearButton = false, showAddButton = false, addButtonLabel, onAddClick, endIcon, showActions = true, onEdit, onDelete, customActions, menuItems, pagination = true, page: controlledPage, rowsPerPage: controlledRowsPerPage = 10, rowsPerPageOptions = [5, 10, 25, 50], onPageChange, onRowsPerPageChange, totalCount, sortBy, sortOrder, onSortChange, emptyMessage, toolbarPosition = 'right', title, maxHeight, customToolbarLeft }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    // Internal pagination state if not controlled
    const [internalPage, setInternalPage] = useState(0);
    const [internalRowsPerPage, setInternalRowsPerPage] = useState(controlledRowsPerPage);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(1280));
    const page = controlledPage !== undefined ? controlledPage : internalPage;
    const rowsPerPage = controlledRowsPerPage !== undefined ? controlledRowsPerPage : internalRowsPerPage;
    // Menu state
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    // Filtered data
    const filteredData = useMemo(() => {
        if (!searchValue || !onSearchChange)
            return data;
        return data.filter((row) => {
            // Default search: check all string values in the row
            return Object.values(row).some((value) => String(value).toLowerCase().includes(searchValue.toLowerCase()));
        });
    }, [data, searchValue, onSearchChange]);
    // Paginated data
    // const paginatedData = useMemo(() => {
    //   if (!pagination) return filteredData;
    //   return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    // }, [filteredData, page, rowsPerPage, pagination]);
    const paginatedData = useMemo(() => {
        return filteredData;
    }, [filteredData]);
    // Handlers
    const handleChangePage = (_event, newPage) => {
        if (onPageChange) {
            onPageChange(newPage);
        }
        else {
            setInternalPage(newPage);
        }
    };
    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        if (onRowsPerPageChange) {
            onRowsPerPageChange(newRowsPerPage);
        }
        else {
            setInternalRowsPerPage(newRowsPerPage);
        }
        if (onPageChange) {
            onPageChange(0);
        }
        else {
            setInternalPage(0);
        }
    };
    const handleMenuOpen = (event, row) => {
        setMenuAnchor(event.currentTarget);
        setSelectedRow(row);
    };
    const handleMenuClose = () => {
        setMenuAnchor(null);
    };
    // Build menu items
    const defaultMenuItems = useMemo(() => {
        if (!onEdit && !onDelete)
            return [];
        const items = [];
        if (onEdit) {
            items.push({
                label: t('actions.edit'),
                onClick: (row) => {
                    if (onEdit)
                        onEdit(row);
                }
            });
        }
        if (onDelete) {
            items.push({
                label: t('actions.delete'),
                onClick: (row) => {
                    if (onDelete)
                        onDelete(row);
                },
                danger: true
            });
        }
        return items;
    }, [onEdit, onDelete, t]);
    const finalMenuItems = menuItems || defaultMenuItems;
    return (_jsxs(Box, { children: [(title || showSearchFilter || showAddButton) && (_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: title
                        ? 'space-between'
                        : toolbarPosition === 'left'
                            ? 'flex-start'
                            : 'flex-end',
                    gap: 2,
                    mb: 3,
                    flexWrap: 'wrap'
                }, children: [title && (_jsx(Typography, { variant: 'h4', color: theme.palette.text.primary, children: title })), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }, children: [customToolbarLeft, showSearchFilter && (_jsx(SearchFilter, { searchValue: searchValue, onSearchChange: onSearchChange, placeholder: searchPlaceholder || t('common.search') || 'Search...', onFilterClick: showFilterButton ? onFilterClick : undefined, filterLabel: filterLabel || t('actions.filters') || 'Filters', breakpoint: 'lg', onClear: () => {
                                    if (onSearchChange) {
                                        onSearchChange('');
                                    }
                                    if (onClearFilters) {
                                        onClearFilters();
                                    }
                                }, showFilterClearButton: showFilterClearButton, handleFilterClear: onClearFilters })), showAddButton && (_jsx(Button, { variant: 'contained', color: 'primary', size: 'large', startIcon: _jsx(Add, { size: 20 }), onClick: onAddClick, endIcon: endIcon, children: addButtonLabel }))] })] })), _jsx(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: isSmallScreen ? 'calc(100dvh - 330px)' : 'calc(100dvh - 294px)'
                }, children: _jsxs(TableContainer, { sx: {
                        flex: 1,
                        overflow: 'auto',
                        position: 'relative',
                        minHeight: 0,
                        borderRadius: '14px',
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`
                    }, children: [_jsxs(Table, { stickyHeader: true, children: [_jsx(TableHead, { children: _jsxs(TableRow, { sx: {
                                            '& .MuiTableCell-head': {
                                                fontWeight: 400,
                                                ...theme.typography.body2,
                                                fontSize: '14px',
                                                color: theme.palette.text.primary,
                                                borderBottom: 'none',
                                                backgroundColor: theme.palette.background.paper,
                                                backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                                                '&:first-of-type': {
                                                    borderTopLeftRadius: theme.spacing(1),
                                                    borderBottomLeftRadius: theme.spacing(1)
                                                },
                                                '&:last-of-type': {
                                                    borderTopRightRadius: theme.spacing(1),
                                                    borderBottomRightRadius: theme.spacing(1)
                                                }
                                            }
                                        }, children: [columns.map((column) => {
                                                const sortField = column.sortKey || column.key;
                                                const canSort = Boolean(column.sortable && onSortChange);
                                                return (_jsx(TableCell, { align: column.align || 'left', sx: { width: column.width }, sortDirection: sortBy === sortField ? sortOrder : false, children: canSort ? (_jsx(TableSortLabel, { active: sortBy === sortField, direction: sortBy === sortField ? sortOrder : 'asc', onClick: () => {
                                                            const nextOrder = sortBy === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
                                                            onSortChange(sortField, nextOrder);
                                                        }, children: column.label })) : (column.label) }, column.key));
                                            }), showActions && (onEdit || onDelete || menuItems) && (_jsx(TableCell, { align: 'right', sx: { width: 120 } }))] }) }), _jsx(TableBody, { children: paginatedData.length === 0 ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: columns.length + (showActions ? 1 : 0), align: 'center', sx: { py: 4 }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: emptyMessage || t('common.noRecordFound') }) }) })) : (paginatedData.map((row, index) => (_jsxs(TableRow, { sx: {
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover
                                            },
                                            '& .MuiTableCell-root': {
                                                borderBottom: 'none'
                                            }
                                        }, children: [columns.map((column) => (_jsx(TableCell, { align: column.align || 'left', children: column.render ? (column.render(row, index)) : (_jsx(Typography, { variant: 'body2', children: row[column.key] })) }, column.key))), showActions && (onEdit || onDelete || menuItems) && (_jsx(TableCell, { align: 'right', children: _jsxs(Box, { sx: { display: 'flex', justifyContent: 'flex-end', gap: 1 }, children: [customActions && customActions(row), onEdit && (_jsx(IconButton, { size: 'small', onClick: () => onEdit(row), sx: {
                                                                color: theme.palette.text.secondary,
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.action.hover
                                                                }
                                                            }, children: _jsx(Iconly, { name: 'Edit', set: 'curved', primaryColor: theme.palette.text.primary }) })), (onDelete || menuItems) && (_jsx(IconButton, { size: 'small', onClick: (e) => handleMenuOpen(e, row), sx: {
                                                                color: theme.palette.text.secondary,
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.action.hover
                                                                }
                                                            }, children: _jsx(Icon, { icon: 'eva:more-horizontal-fill', width: 18 }) }))] }) }))] }, getRowId(row))))) })] }), pagination && (_jsx(Box, { sx: {
                                position: 'sticky',
                                bottom: 0
                            }, children: _jsx(TablePagination, { component: 'div', count: totalCount ?? filteredData.length, page: page, onPageChange: handleChangePage, rowsPerPage: rowsPerPage, onRowsPerPageChange: handleChangeRowsPerPage, rowsPerPageOptions: rowsPerPageOptions, labelRowsPerPage: t('dataGrid.rowsPerPage') || 'Rows per page:', labelDisplayedRows: ({ from, to, count }) => `${from}-${to} ${t('dataGrid.of') || 'of'} ${count !== -1 ? count : `more than ${to}`}` }) }))] }) }), showActions && finalMenuItems.length > 0 && (_jsx(StyledMenu, { anchorEl: menuAnchor, open: Boolean(menuAnchor), onClose: handleMenuClose, actions: finalMenuItems
                    .filter((item) => {
                    // Filter out items that should be disabled (remove them from menu)
                    if (selectedRow && item.disabled) {
                        return !item.disabled(selectedRow);
                    }
                    return true;
                })
                    .map((item) => ({
                    text: item.label,
                    to: () => {
                        if (selectedRow) {
                            item.onClick(selectedRow);
                            handleMenuClose();
                        }
                    },
                    icon: item.icon,
                    color: item.color,
                    danger: item.danger
                })) }))] }));
};
export default DataTable;
